import moment from 'moment'
import { TagawattDataset } from './core/_models'

export const CARD_ID_PREFIX = 'card:'

/** Half-hour labels aligned with readings table: 00:30 … 00:00 */
export function getHalfHourLabelTimestamp(
  viewDate: string,
  label: string,
  labelIndex: number,
  totalLabels: number
): number {
  if (label === '00:00' && labelIndex === totalLabels - 1) {
    return moment(viewDate, 'YYYY-MM-DD').add(1, 'day').startOf('day').valueOf()
  }
  return moment(`${viewDate} ${label}`, 'YYYY-MM-DD HH:mm').valueOf()
}

export function trimHalfHourLabelsAndDataToNow(
  labels: string[],
  data: number[]
): { labels: string[]; data: number[] } {
  const nowMinutes = moment().hours() * 60 + moment().minutes()
  const maxIndex = Math.max(0, Math.min(47, Math.ceil(nowMinutes / 30) - 1))
  return {
    labels: labels.slice(0, maxIndex + 1),
    data: data.slice(0, maxIndex + 1),
  }
}

/** End-of-day 00:00 minute key is only visible near midnight on today views. */
export function isMinuteKeyIncludedForToday(minuteKey: string, nowKey?: string): boolean {
  const now = nowKey ?? moment().format('HH:mm')
  if (minuteKey === '00:00') {
    const nowMinutes = moment().hours() * 60 + moment().minutes()
    return nowMinutes >= 23 * 60 + 59
  }
  return minuteKey <= now
}

/** Resolution-independent daily total for summary cards. */
export function sumDatasetDailyTotal(ds: TagawattDataset, isTodayView: boolean): number {
  if (typeof ds.total === 'number' && !Number.isNaN(ds.total)) {
    return ds.total
  }

  if (ds.minute_data && Object.keys(ds.minute_data).length > 0) {
    const nowKey = moment().format('HH:mm')
    return Object.entries(ds.minute_data).reduce((sum, [key, value]) => {
      if (isTodayView && !isMinuteKeyIncludedForToday(key, nowKey)) return sum
      return sum + (Number(value) || 0)
    }, 0)
  }

  const values = ds.data || []
  if (values.length === 0) return 0

  if (isTodayView) {
    return trimHalfHourLabelsAndDataToNow([], values).data.reduce(
      (sum, value) => sum + (Number(value) || 0),
      0
    )
  }

  return values.reduce((sum, value) => sum + (Number(value) || 0), 0)
}

export function getSeriesKey(ds: TagawattDataset): string {
  if (ds.source === 'readings') {
    return `readings:${ds.name}`
  }
  return `tag:${ds.sensor_code || ds.name}`
}

export function getCardIdForSeries(seriesKey: string): string {
  return `${CARD_ID_PREFIX}${seriesKey}`
}

export function getSeriesKeyFromCardId(cardId: string): string | null {
  if (!cardId.startsWith(CARD_ID_PREFIX)) return null
  return cardId.slice(CARD_ID_PREFIX.length)
}

export function filterVisibleDatasets(
  datasets: TagawattDataset[],
  hiddenSeriesKeys: Set<string>
): TagawattDataset[] {
  return datasets.filter((ds) => !hiddenSeriesKeys.has(getSeriesKey(ds)))
}

export function getCardSeriesKeys(cardId: string): string[] {
  const seriesKey = getSeriesKeyFromCardId(cardId)
  return seriesKey ? [seriesKey] : []
}

export function isCardSeriesVisible(cardId: string, hiddenSeriesKeys: Set<string>): boolean {
  const keys = getCardSeriesKeys(cardId)
  if (keys.length === 0) return true
  return keys.every((key) => !hiddenSeriesKeys.has(key))
}

export function toggleCardVisibility(cardId: string, hiddenSeriesKeys: Set<string>): Set<string> {
  const keys = getCardSeriesKeys(cardId)
  if (keys.length === 0) return hiddenSeriesKeys

  const next = new Set(hiddenSeriesKeys)
  const allVisible = keys.every((key) => !next.has(key))

  if (allVisible) {
    keys.forEach((key) => next.add(key))
  } else {
    keys.forEach((key) => next.delete(key))
  }

  return next
}

export function toggleSeriesVisibility(
  seriesKey: string,
  hiddenSeriesKeys: Set<string>
): Set<string> {
  const next = new Set(hiddenSeriesKeys)
  if (next.has(seriesKey)) {
    next.delete(seriesKey)
  } else {
    next.add(seriesKey)
  }
  return next
}
